import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  calculateWinData,
  calculateWinDataForLimit6To12,
  calculateWinDataForLimit4To17,
  calculateWinDataForLimit5To13} from '../components/Calculations';

const GameContext = createContext();

const GameProvider = ({ children }) => {
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [againstNumbers, setAgainstNumbers] = useState([]);
  const [limit, setLimit] = useState([3, 24]);
  const [amount, setAmount] = useState('2.00');
  const [price, setPrice] = useState(0);
  const [minimumWin, setMinimumWin] = useState(0);
  const [maximumWin, setMaximumWin] = useState(0);
  const [lines, setLines] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [winNumbers, setWinNumbers] = useState([]);
  const [result, setResult] = useState(null);
  const [gameOutcome, setGameOutcome] = useState('pending');
  const [selectedDay, setSelectedDay] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [data, setData] = useState(null);

  const closeModal = () => {
    setModalVisible(false);
    setData(null);
  };

  const getLimitLabel = useCallback((limit) => {
    switch (JSON.stringify(limit)) {
      case JSON.stringify([1, 2]):
        return 'DIRECT 2';
      case JSON.stringify([1, 3]):
        return 'DIRECT 3';
      case JSON.stringify([3, 24]):
        return 'PERM 2';
      case JSON.stringify([4, 17]):
        return 'PERM 3';
      case JSON.stringify([5, 13]):
        return 'PERM 4';
      case JSON.stringify([6, 12]):
        return 'PERM 5';
      case JSON.stringify([1, 1]):
        return 'Banker Against All';
      case JSON.stringify([1, 24]):
        return 'Banker Against';
      default:
        return '';
    }
  }, []);

  const limitLabel = useMemo(() => getLimitLabel(limit), [getLimitLabel, limit]);

  useEffect(() => {
    setSelectedNumbers([]);
    setAgainstNumbers([]);
  }, [limit]);

  useEffect(() => {
    const n = selectedNumbers.length;
    const n1 = againstNumbers.length;

    let calculatedLines = 0;

    if (n === 2 && limit[1] === 2) {
      setMinimumWin(amount * 220);
      setPrice(amount);
      setLines(1);
      setMaximumWin(0);
    } else if (n === 3 && limit[1] === 3) {
      setPrice(amount);
      setMinimumWin(amount * 1800);
      setLines(1);
      setMaximumWin(0);
    } else if (n >= 3 && n <= 24 && limit[0] === 3 && limit[1] === 24) {
      calculatedLines = ((n - 1) * n) / 2;
      setLines(calculatedLines);
      setPrice(calculatedLines * amount);
      setMinimumWin(amount * 220);
      if (n <= 5) {
        setMaximumWin(calculatedLines * 220 * amount);
      } else {
        setMaximumWin(2200 * amount);
      }
    } else if (n >= 4 && n <= 17 && limit[0] === 4 && limit[1] === 17) {
      const { calculatedLines, minimumWin, price, maximumWin } = calculateWinDataForLimit4To17(n, amount);

      setLines(calculatedLines);
      setPrice(price);
      setMinimumWin(minimumWin);
      setMaximumWin(maximumWin);
    } else if (n >= 5 && n <= 13 && limit[0] === 5 && limit[1] === 13) {
      const { calculatedLines, minimumWin, price } = calculateWinDataForLimit5To13(n, amount);

      setLines(calculatedLines);
      setPrice(price);
      setMinimumWin(minimumWin);
    } else if (n >= 6 && n <= 12 && limit[0] === 6 && limit[1] === 12) {
      const { calculatedLines, minimumWin, price } = calculateWinDataForLimit6To12(n, amount);

      setLines(calculatedLines);
      setPrice(price);
      setMinimumWin(minimumWin);
    } else if (n === 1 && limit[1] === 1) {
      calculatedLines = 89;
      setPrice(amount * calculatedLines);
      setMinimumWin(amount * 880);
      setLines(89);
    } else if (limit[0] === 1 && limit[1] === 24) {
      if (selectedOption === 'Against') {
        const { calculatedLines, minimumWin, price, maximumWin } = calculateWinData(
          againstNumbers.length,
          selectedNumbers.length,
          n,
          amount
        );
        setMinimumWin(minimumWin);
        setPrice(price);
        setLines(calculatedLines);
        setMaximumWin(maximumWin);
      }
    } else {
      setPrice(0);
      setMinimumWin(0);
      setMaximumWin(0);
      setLines(0);
      return;
    }
  }, [selectedOption, amount, selectedNumbers, againstNumbers, limit]);

  const [remainingNumbers, setRemainingNumbers] = useState(limit[1] - selectedNumbers.length);

  const handleNumberPress = useCallback(
    (number, isSelected, isAgainstSelected) => {
      if (isSelected) {
        setSelectedNumbers(selectedNumbers.filter((n) => n !== number));
        if (isAgainstSelected) {
          setAgainstNumbers(againstNumbers.filter((n) => n !== number));
        }
      } else {
        if (remainingNumbers > 0 && !isAgainstSelected) {
          setSelectedNumbers([...selectedNumbers, number]);
          setRemainingNumbers((prevRemaining) => prevRemaining - 1);
        }
      }
    },
    [selectedNumbers, setSelectedNumbers, againstNumbers, setAgainstNumbers, remainingNumbers]
  );

  useEffect(() => {
    setRemainingNumbers(limit[1] - selectedNumbers.length);
  }, [limit, selectedNumbers]);

  const resetNumbers = useCallback(() => {
    setSelectedNumbers([]);
    setAgainstNumbers([]);
    setSelectedOption('');
  }, [setSelectedNumbers, setAgainstNumbers, setSelectedOption]);

  // Reset all game state (selected numbers, against numbers, amount, option, etc)
  const resetGame = useCallback(() => {
    setSelectedNumbers([]);
    setAgainstNumbers([]);
    setSelectedOption('');
    setAmount('5');
    setPrice(0);
    setMinimumWin(0);
    setMaximumWin(0);
    setLines(0);
    setWinNumbers([]);
    setResult(null);
    setGameOutcome('pending');
    setLimit([1, 2]);
    setSelectedDay([]);
    setModalVisible(false);
    setData(null);
    setRemainingNumbers(0);
  }, []);

  //validation function
  const validateNumbers = (selectedNumbers, againstNumbers, amount, price, userBalance, limit) => {
    if (
      limit.includes(1) &&
      limit.includes(2) &&
      selectedNumbers.length !== 2
    ) {
      return "Please select exactly 2 numbers";
    } else if (
      limit.includes(1) &&
      limit.includes(3) &&
      selectedNumbers.length !== 3
    ) {
      return "Please select exactly 3 numbers";
    } else if (
      limit.includes(3) &&
      limit.includes(24) &&
      (selectedNumbers.length < 3 || selectedNumbers.length > 24)
    ) {
      return "Please select between 3 and 24 numbers";
    } else if (
      limit.includes(4) &&
      limit.includes(17) &&
      (selectedNumbers.length < 4 || selectedNumbers.length > 17)
    ) {
      return "Please select between 4 and 17 numbers";
    } else if (
      limit.includes(5) &&
      limit.includes(13) &&
      (selectedNumbers.length < 5 || selectedNumbers.length > 13)
    ) {
      return "Please select between 5 and 13 numbers";
    } else if (
      limit.includes(6) &&
      limit.includes(12) &&
      (selectedNumbers.length < 6 || selectedNumbers.length > 12)
    ) {
      return "Please select between 6 and 12 numbers";
    } else if (
      limit.includes(1) &&
      limit.includes(24) &&
      (againstNumbers.length < 1 ||
        againstNumbers.length > 24 ||
        selectedNumbers.length < 1 ||
        selectedNumbers.length > 24)
    ) {
      return "Please select at least 1 number in both against and selected numbers, and ensure they are between 1 and 24";
    } else if (
      limit.includes(1) &&
      limit.includes(1) &&
      !limit.includes(2) &&
      !limit.includes(3) &&
      !limit.includes(24) &&
      selectedNumbers.length !== 1
    ) {
      return "Please select exactly 1 number";
    } else if (amount < 0.2) {
      return "The minimum amount you can stake is 0.2";
    }
    // You can add more checks if needed
    return null; 
  };

  return (
    <GameContext.Provider
      value={{
        limit,
        setLimit,
        limitLabel,
        selectedNumbers,
        againstNumbers,
        setAgainstNumbers,
        setSelectedNumbers,
        resetNumbers,
        resetGame, 
        amount,
        setAmount,
        minimumWin,
        maximumWin,
        price,
        selectedOption,
        setSelectedOption,
        lines,
        handleNumberPress,
        remainingNumbers,
        setRemainingNumbers,
        modalVisible,
        setModalVisible,
        closeModal,
        setData,
        data,
        winNumbers,
        setWinNumbers,
        result,
        setResult,
        gameOutcome,
        setGameOutcome,
        validateNumbers,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export { GameContext, GameProvider };
